// MARK://dhk8/admin-shell/pages/modules/ProductDetailPage
// Ficha "ver-primero" de un producto del catálogo (Gustavo 2026-06-22). Header con
// foto + nombre + SKU + pills (estado / con receta) + KPIs (precio, y costo/margen si
// hay receta costeada) + Tabs (General, Receta, Precios, Variantes, Inventario). El
// tab General se ve primero; "Editar" abre el SectionedSheet. Carga el producto por
// id con los clients existentes (catalog.ts); la receta del producto se resuelve por
// recipes-catalog.listRecipes (match por productId).
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ChevronLeft, Pencil } from 'lucide-react'

import * as cat from '../../lib/clients/catalog.js'
import * as rec from '../../lib/clients/recipes-catalog.js'
import * as inv from '../../lib/clients/inventory.js'
import type { Product } from '../../lib/mock-data.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import { mxn, parseMxnToCents } from '../../lib/money.js'
import EntityDetailPage from '../../components/EntityDetailPage.js'
import TaxRatePicker from '../../components/TaxRatePicker.js'
import StatusPill, { lifecycleTone } from '../../components/StatusPill.js'
import { SummaryStrip } from '../../components/StatCard.js'
import Tabs from '../../components/Tabs.js'
import SectionedSheet, { FormSection } from '../../components/SectionedSheet.js'
import UnitPicker, { unitShort } from '../../components/UnitPicker.js'
import SatCodePicker from '../../components/SatCodePicker.js'
import ImageUploader from '../../components/ImageUploader.js'
import AuditTrail from '../../components/AuditTrail.js'
import { useToast } from '../../components/Toast.js'

type Tab = 'general' | 'receta' | 'precios' | 'variantes' | 'inventario' | 'historial'

export default function ProductDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()

  const product = useAsync((s) => cat.getProduct(id, s), [id])
  const cats = useAsync((s) => cat.listCategories(s))
  const recipes = useAsync((s) => rec.listRecipes(s))
  const priceLists = useAsync((s) => cat.listPriceLists(s))
  const axes = useAsync((s) => (id ? cat.listVariantAxes(id, s) : Promise.resolve([])), [id])
  const skus = useAsync((s) => (id ? cat.listVariantSkus(id, s) : Promise.resolve([])), [id])
  const inventory = useAsync((s) => inv.listInventory(s), [id])

  const [tab, setTab] = useState<Tab>('general')
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState<Product | null>(null)
  const [busy, setBusy] = useState(false)

  const p = product.data
  const catName = (cid: string) => cats.data?.find((c) => c.id === cid)?.name ?? cid
  // Receta vinculada a este producto (si existe). Da el costo → margen.
  const recipe = useMemo(() => (recipes.data ?? []).find((r) => r.productId === id) ?? null, [recipes.data, id])
  const hasCost = recipe?.cost != null
  const marginPct = hasCost && p && p.priceCents > 0 ? Math.round(((p.priceCents - (recipe!.cost as number)) / p.priceCents) * 100) : null

  function openEdit() { if (p) { setForm({ ...p }); setEditing(true) } }
  async function save() {
    if (!form) return
    setBusy(true)
    try { await cat.updateProduct(form); toast('Producto actualizado', 'success'); setEditing(false); product.reload() }
    catch (e) { toast(e instanceof Error ? e.message : 'No se pudo guardar', 'danger') } finally { setBusy(false) }
  }

  const kpis = useMemo(() => {
    if (!p) return []
    const items: { label: string; value: React.ReactNode; tone?: 'ok' | 'warn' | 'danger' | 'info' | 'neutral' }[] = [
      { label: 'Precio', value: mxn(p.priceCents) },
    ]
    if (hasCost) {
      items.push({ label: 'Costo', value: mxn(recipe!.cost as number) })
      if (marginPct != null) items.push({ label: 'Margen', value: `${marginPct}%`, tone: marginPct >= 0 ? 'ok' : 'danger' })
    }
    return items
  }, [p, hasCost, recipe, marginPct])

  // Overrides de precio de este producto en las listas de precios.
  const productPriceListEntries = useMemo(() => {
    return (priceLists.data ?? [])
      .map((pl) => ({ pl, price: pl.overrides[id] }))
      .filter((x) => x.price !== undefined) as { pl: { id: string; name: string; active: boolean }; price: number }[]
  }, [priceLists.data, id])

  const productInventory = useMemo(() => (inventory.data ?? []).filter((it) => it.productId === id), [inventory.data, id])

  const optionLabel = (optionId: string): string => {
    for (const a of axes.data ?? []) { const o = a.options.find((x) => x.id === optionId); if (o) return `${a.name}: ${o.label}` }
    return optionId
  }

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => navigate('/catalog')} aria-label="Volver al catálogo"><ChevronLeft size={18} /></button>
          <div><h1 className="adm-page__title">Producto</h1><p className="adm-page__sub">Catálogo · ficha del producto</p></div>
        </div>
      </div>

      {asyncState(product.loading, product.error, product.reload)}

      {p && (
        <EntityDetailPage
          title={p.name}
          subtitle={`SKU ${p.sku || '—'} · ${catName(p.categoryId)}`}
          image={p.imageUrl ?? null}
          pills={<>
            <StatusPill tone={lifecycleTone(p.available)} label={p.available ? 'Disponible' : 'Agotado'} />
            {p.consumesRecipe && <StatusPill tone="info" label="Con receta" />}
            {p.tracksInventory && <StatusPill tone="neutral" label="Lleva inventario" />}
          </>}
          kpis={kpis.length > 0 ? <SummaryStrip items={kpis} /> : undefined}
          actions={<button className="adm-btn adm-btn--primary adm-btn--sm" onClick={openEdit}><Pencil size={15} /> Editar</button>}
          tabs={<Tabs
            tabs={[
              { id: 'general', label: 'General' },
              { id: 'receta', label: 'Receta' },
              { id: 'precios', label: 'Precios' },
              { id: 'variantes', label: 'Variantes', count: skus.data?.length },
              { id: 'inventario', label: 'Inventario' },
              { id: 'historial', label: 'Historial' },
            ]}
            active={tab}
            onChange={(t) => setTab(t as Tab)}
            ariaLabel="Secciones del producto"
          />}
        >
          {tab === 'general' && (
            <div className="adm-card adm-card--pad" style={{ display: 'grid', gap: 14, maxWidth: 560 }}>
              <Field label="Nombre">{p.name}</Field>
              <Field label="SKU"><span className="adm-mono">{p.sku || '—'}</span></Field>
              <Field label="Categoría">{catName(p.categoryId)}</Field>
              <Field label="Descripción">{p.description || <span className="adm-muted">Sin descripción</span>}</Field>
              <Field label="Precio"><span className="adm-mono">{mxn(p.priceCents)}</span></Field>
              <Field label="Unidad">{unitShort(p.unit) || <span className="adm-muted">—</span>}</Field>
              <Field label="Clave SAT producto"><span className="adm-mono">{p.satProductKey || '—'}</span></Field>
              <Field label="Clave SAT unidad"><span className="adm-mono">{p.satUnitKey || '—'}</span></Field>
              {p.type && <Field label="Tipo"><span className="adm-mono">{p.type}</span></Field>}
            </div>
          )}

          {tab === 'receta' && (
            <div className="adm-card adm-card--pad" style={{ display: 'grid', gap: 12, maxWidth: 560 }}>
              {recipes.loading && <p className="adm-muted">Cargando receta…</p>}
              {!recipes.loading && recipe && (
                <>
                  <Field label="Receta vinculada">{recipe.name}</Field>
                  <Field label="Costo total"><span className="adm-mono">{recipe.cost != null ? mxn(recipe.cost) : '—'}</span></Field>
                  <Field label="Ingredientes">{recipe.ingredients.length}</Field>
                  <div><button className="adm-btn adm-btn--primary adm-btn--sm" onClick={() => navigate(`/recipes/${recipe.id}`)}>Ver receta</button></div>
                </>
              )}
              {!recipes.loading && !recipe && (
                <p className="adm-muted">Este producto no tiene una receta vinculada. {p.consumesRecipe ? 'Está marcado como "consume receta": vincula una receta desde el recetario.' : ''}</p>
              )}
            </div>
          )}

          {tab === 'precios' && (
            <div className="adm-card adm-card--pad0">
              <div className="adm-list">
                <div className="adm-row">
                  <div className="adm-row__main"><div className="adm-row__title">Precio base</div><div className="adm-row__meta">Precio del catálogo</div></div>
                  <div className="adm-row__tail"><span className="adm-mono">{mxn(p.priceCents)}</span></div>
                </div>
                {priceLists.loading && <div className="adm-empty">Cargando listas de precios…</div>}
                {!priceLists.loading && productPriceListEntries.length === 0 && <div className="adm-empty">Sin precios especiales en listas de precios.</div>}
                {productPriceListEntries.map(({ pl, price }) => (
                  <div key={pl.id} className="adm-row">
                    <div className="adm-row__main"><div className="adm-row__title">{pl.name}</div><div className="adm-row__meta">Lista de precios</div></div>
                    <div className="adm-row__tail">
                      <StatusPill tone={lifecycleTone(pl.active)} label={pl.active ? 'Activa' : 'Inactiva'} />
                      <span className="adm-mono">{mxn(price)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === 'variantes' && (
            <div className="adm-card adm-card--pad0">
              {asyncState(axes.loading, axes.error, axes.reload)}
              <div className="adm-list">
                {!axes.loading && (axes.data ?? []).length === 0 && (skus.data ?? []).length === 0 && (
                  <div className="adm-empty">Sin variantes. Configúralas en la pestaña Variantes del catálogo.</div>
                )}
                {(axes.data ?? []).map((a) => (
                  <div key={a.id} className="adm-row">
                    <div className="adm-row__main">
                      <div className="adm-row__title">{a.name}</div>
                      <div className="adm-row__meta">{a.options.map((o) => `${o.label} (${o.priceDeltaCents >= 0 ? '+' : ''}${mxn(o.priceDeltaCents)})`).join(' · ') || 'sin opciones'}</div>
                    </div>
                  </div>
                ))}
                {(skus.data ?? []).map((s) => (
                  <div key={s.id} className="adm-row">
                    <div className="adm-row__main">
                      <div className="adm-row__title">{s.sku}</div>
                      <div className="adm-row__meta">{s.optionIds.map(optionLabel).join(' · ') || 'sin opciones'}</div>
                    </div>
                    <div className="adm-row__tail"><StatusPill tone={lifecycleTone(s.active)} label={s.active ? 'Activo' : 'Inactivo'} /></div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === 'inventario' && (
            <div className="adm-card adm-card--pad0">
              {asyncState(inventory.loading, inventory.error, inventory.reload)}
              <div className="adm-list">
                {!inventory.loading && !p.tracksInventory && productInventory.length === 0 && (
                  <div className="adm-empty">Este producto no lleva inventario propio.</div>
                )}
                {!inventory.loading && p.tracksInventory && productInventory.length === 0 && (
                  <div className="adm-empty">Sin existencias registradas para este producto.</div>
                )}
                {productInventory.map((it) => (
                  <div key={it.id} className="adm-row">
                    <div className="adm-row__main"><div className="adm-row__title">{it.name}</div><div className="adm-row__meta">por {unitShort(it.unit)} · nivel de reorden {it.reorderLevel}</div></div>
                    <div className="adm-row__tail">
                      <StatusPill tone={it.status === 'OK' ? 'ok' : it.status === 'LOW' ? 'warn' : 'danger'} label={it.status === 'OK' ? 'En stock' : it.status === 'LOW' ? 'Bajo stock' : 'Agotado'} />
                      <span className="adm-mono">{it.onHand}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === 'historial' && <AuditTrail entityId={p.id} />}
        </EntityDetailPage>
      )}

      {form && (
        <SectionedSheet
          open={editing}
          title="Editar producto"
          onClose={() => setEditing(false)}
          footer={<>
            <button className="adm-btn" onClick={() => setEditing(false)} disabled={busy}>Cancelar</button>
            <button className="adm-btn adm-btn--primary" onClick={save} disabled={busy || !form.name.trim()}>{busy ? 'Guardando…' : 'Guardar'}</button>
          </>}
        >
          <FormSection title="Identidad">
            <label className="adm-field"><span className="adm-field__label">Nombre</span><input className="adm-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
            <label className="adm-field"><span className="adm-field__label">SKU</span><input className="adm-input" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} /></label>
            <label className="adm-field"><span className="adm-field__label">Descripción</span><textarea className="adm-input" rows={2} value={form.description ?? ''} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
            <label className="adm-field"><span className="adm-field__label">Categoría</span>
              <select className="adm-select" value={form.categoryId} onChange={(e) => setForm({ ...form, categoryId: e.target.value })}>{(cats.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></label>
          </FormSection>
          <FormSection title="Precio e impuestos">
            <label className="adm-field"><span className="adm-field__label">Precio (MXN)</span><input className="adm-input" inputMode="decimal" value={(form.priceCents / 100).toString()} onChange={(e) => setForm({ ...form, priceCents: parseMxnToCents(e.target.value) })} /></label>
            <TaxRatePicker value={form.taxRate} onChange={(taxRate) => setForm({ ...form, taxRate })} />
            <UnitPicker label="Unidad" value={form.unit ?? ''} onChange={(unit) => setForm({ ...form, unit })} />
            <div className="adm-field"><span className="adm-field__label">Clave SAT producto (claveProdServ)</span><SatCodePicker value={form.satProductKey ?? ''} onChange={(satProductKey) => setForm({ ...form, satProductKey })} /></div>
            <UnitPicker mode="sat" label="Clave SAT unidad (claveUnidad)" value={form.satUnitKey ?? ''} onChange={(satUnitKey) => setForm({ ...form, satUnitKey })} />
          </FormSection>
          <FormSection title="Operación">
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}><input type="checkbox" checked={form.available} onChange={(e) => setForm({ ...form, available: e.target.checked })} /> Disponible</label>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}><input type="checkbox" checked={form.tracksInventory ?? false} onChange={(e) => setForm({ ...form, tracksInventory: e.target.checked })} /> Lleva inventario propio</label>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}><input type="checkbox" checked={form.consumesRecipe ?? false} onChange={(e) => setForm({ ...form, consumesRecipe: e.target.checked })} /> Consume receta al vender</label>
          </FormSection>
          <FormSection title="Media">
            <div className="adm-field"><span className="adm-field__label">Imagen del producto</span>
              <ImageUploader value={form.imageUrl ?? null} onChange={(url) => setForm({ ...form, imageUrl: url, imageKey: url ? form.imageKey : null })} />
            </div>
          </FormSection>
        </SectionedSheet>
      )}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--pos-fg-tertiary)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--pos-fg)' }}>{children}</div>
    </div>
  )
}
